import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { User } from 'src/constants';
import { AppDispatch } from 'src/store/configureStore';

// Selectors
export const selectOptimisticUpdateCount = (
  state: { optimisticUpdates: OptimisticUpdateState },
  entityType: string,
  itemId: string,
) => state.optimisticUpdates.counts[entityType]?.[itemId] || 0;

export const selectHasPendingMutations = (
  optimisticUpdates: OptimisticUpdateState,
  entityType: string,
  itemId: string,
) => (optimisticUpdates.counts[entityType]?.[itemId] || 0) > 0;

/**
 * Check if a WebSocket message should be processed based on mutation counts.
 * If an item has pending mutations, it decrements the count and returns false.
 * If an item has no pending mutations, it returns true.
 *
 * @param entity - The entity type (e.g., 'CLIENT_USER', 'COMPANY')
 * @param items - Array of items in the WebSocket message
 * @param event - The WebSocket event type
 * @param dispatch - Redux dispatch function
 * @param currentState - Current Redux state
 * @returns true if the message should be processed, false if it should be skipped
 */
export const shouldProcessWebSocketMessage = (
  entity: string,
  items: Array<User>,
  dispatch: AppDispatch,
  optimisticUpdates: OptimisticUpdateState,
): boolean => {
  // Check each item in the message
  for (const item of items) {
    if (!item.id) {
      // If item has no ID, we can't track optimistic updates for it, so process it
      continue;
    }
    const hasPendingMutations = selectHasPendingMutations(
      optimisticUpdates,
      entity,
      item.id,
    );

    if (hasPendingMutations) {
      // Item has pending optimistic updates, decrement count and skip processing
      dispatch(decrementUpdate({ entityType: entity, itemId: item.id }));
      return false;
    }
  }

  // No pending mutations found, allow processing
  return true;
};

export interface OptimisticUpdateState {
  // Track mutation counts by entity type and item ID
  // Format: { [entityType]: { [itemId]: count } }
  counts: Record<string, Record<string, number>>;
}

const initialState: OptimisticUpdateState = {
  counts: {},
};

/**
 * This slice tracks the number of mutations for each entity type and item ID.
 * It's used to avoid updating the Redux store with stale data—for example,
 * ignoring outdated WebSocket messages when the state has already been updated optimistically.
 */
const optimisticUpdatesSlice = createSlice({
  name: 'optimisticUpdates',
  initialState,
  reducers: {
    addUpdate: (
      state,
      action: PayloadAction<{ entityType: string; itemId: string }>,
    ) => {
      const { entityType, itemId } = action.payload;
      if (!state.counts[entityType]) {
        state.counts[entityType] = {};
      }
      state.counts[entityType][itemId] =
        (state.counts[entityType][itemId] || 0) + 1;
    },
    decrementUpdate: (
      state,
      action: PayloadAction<{ entityType: string; itemId: string }>,
    ) => {
      const { entityType, itemId } = action.payload;
      if (!state.counts[entityType] || !state.counts[entityType][itemId]) {
        return;
      }
      state.counts[entityType][itemId] = Math.max(
        0,
        state.counts[entityType][itemId] - 1,
      );
      // Clean up if count reaches 0
      if (state.counts[entityType][itemId] === 0) {
        delete state.counts[entityType][itemId];
        // Clean up empty entity type
        if (Object.keys(state.counts[entityType]).length === 0) {
          delete state.counts[entityType];
        }
      }
    },
    clearUpdates: (
      state,
      action: PayloadAction<{ entityType?: string; itemId?: string }>,
    ) => {
      const { entityType, itemId } = action.payload;
      if (entityType && itemId) {
        // Clear specific item
        if (state.counts[entityType]) {
          delete state.counts[entityType][itemId];
          if (Object.keys(state.counts[entityType]).length === 0) {
            delete state.counts[entityType];
          }
        }
      } else if (entityType) {
        // Clear all items for entity type
        delete state.counts[entityType];
      } else {
        // Clear all mutations
        state.counts = {};
      }
    },
  },
});

export const { addUpdate, decrementUpdate, clearUpdates } =
  optimisticUpdatesSlice.actions;

export default optimisticUpdatesSlice.reducer;
